import type { Entitlement, XeroInvoiceState } from '../../types'

interface EntitlementsListProps {
  entitlements: Entitlement[]
  xeroStates: Map<string, XeroInvoiceState>
  selectedEntitlementId: string | null
  onSelect: (id: string) => void
}

function entitlementStatusBadgeClass(status: string): string {
  if (status === 'active') {
    return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  }
  return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
}

function gateBadgeClass(passed: boolean): string {
  if (passed) {
    return 'bg-[#4e5e82]/10 text-[#5c6e94] dark:bg-[#8a9bba]/15 dark:text-[#8a9bba]'
  }
  return 'bg-stone-100 text-stone-500 dark:bg-stone-700 dark:text-stone-400'
}

interface GateBadgeProps {
  passed: boolean
  letter: string
}

function GateBadge({ passed, letter }: GateBadgeProps): React.ReactElement {
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium ${gateBadgeClass(passed)}`}
    >
      {letter}
    </span>
  )
}

interface GateProgressProps {
  entitlement: Entitlement
}

function GateProgress({ entitlement }: GateProgressProps): React.ReactElement {
  return (
    <div className="mt-2 flex items-center gap-1">
      <GateBadge passed={entitlement.paymentGatePassed} letter="P" />
      <span className="text-stone-300 dark:text-stone-600">→</span>
      <GateBadge passed={entitlement.licenseGatePassed} letter="L" />
      <span className="text-stone-300 dark:text-stone-600">→</span>
      <GateBadge passed={entitlement.status === 'active'} letter="A" />
    </div>
  )
}

interface EntitlementRowProps {
  entitlement: Entitlement
  hasXeroData: boolean
  isSelected: boolean
  onSelect: (id: string) => void
}

function EntitlementRowHeader({
  entitlement,
  hasXeroData,
}: {
  entitlement: Entitlement
  hasXeroData: boolean
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-stone-900 dark:text-white">
          {entitlement.planTierGranted}
        </p>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {entitlement.seatsUsed}/{entitlement.seatsGranted} seats
        </p>
      </div>
      <div className="flex items-center gap-2">
        {hasXeroData && (
          <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            Xero
          </span>
        )}
        <span
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${entitlementStatusBadgeClass(entitlement.status)}`}
        >
          {entitlement.status}
        </span>
      </div>
    </div>
  )
}

function EntitlementRow({
  entitlement,
  hasXeroData,
  isSelected,
  onSelect,
}: EntitlementRowProps): React.ReactElement {
  const className = isSelected
    ? 'bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10 border-l-2 border-[#4e5e82]'
    : 'hover:bg-stone-50 dark:hover:bg-stone-700/50'
  return (
    <button
      onClick={() => {
        onSelect(entitlement.id)
      }}
      className={`w-full px-4 py-3 text-left transition-colors ${className}`}
    >
      <EntitlementRowHeader entitlement={entitlement} hasXeroData={hasXeroData} />
      <GateProgress entitlement={entitlement} />
    </button>
  )
}

export function EntitlementsList(props: EntitlementsListProps): React.ReactElement {
  const { entitlements, xeroStates, selectedEntitlementId, onSelect } = props
  return (
    <div className="rounded-xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <div className="border-b border-stone-200 px-4 py-3 dark:border-stone-700">
        <h3 className="font-semibold text-stone-900 dark:text-white">Entitlements</h3>
        <p className="text-xs text-stone-500 dark:text-stone-400">{entitlements.length} total</p>
      </div>
      <div className="max-h-96 divide-y divide-stone-200 overflow-y-auto dark:divide-stone-700">
        {entitlements.length === 0 ? (
          <div className="p-4 text-center text-sm text-stone-500 dark:text-stone-400">
            No entitlements found
          </div>
        ) : (
          entitlements.map((entitlement) => (
            <EntitlementRow
              key={entitlement.id}
              entitlement={entitlement}
              hasXeroData={xeroStates.has(entitlement.id)}
              isSelected={selectedEntitlementId === entitlement.id}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  )
}
